import { computed, ref, unref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { MaybeRef } from '@vueuse/core'
import { useGetExtensionSchema } from '@/api/composables/schemas'
import { isEmpty } from 'lodash'
import { CustomFieldsSchema } from '@/models/types/custom-fields-schema'
import { mergeJsonSchemas } from '@/utils/json-schema-merger.js'
import Task from '@/models/task'
import Division from '@/models/division'

/**
 * Builds a merged schema from activity, division, and base custom fields schemas.
 *
 * This is a pure function that takes all the necessary pieces to construct
 * a complete CustomFieldsSchema, handling the fallback logic for legacy jobs
 * that don't have an extensionSchemaId.
 *
 * @param division - The job's division with its jobCustomFieldsSchema
 * @param task - The task associated with the resource-type-activity (for fallback schema) of a job.
 * @param customFieldsSchema - The base custom fields schema from the store
 * @param currentExtensionSchemaId - The current schema version ID to use
 * @param currentExtensionSchema - The fetched schema data for the current version (with id = currentExtensionSchemaId)
 * @returns A merged CustomFieldsSchema with both schema and uiSchema
 */
export const buildRootSchema = (
  division: Division | null | undefined,
  task: Task | null | undefined,
  customFieldsSchema: any,
  currentExtensionSchemaId: number | null | undefined,
  currentExtensionSchema: any,
): CustomFieldsSchema => {
  if (isEmpty(customFieldsSchema) || !division || !task) {
    return {
      schema: {} as any,
      uiSchema: {},
    }
  }

  const hasCustomFields = currentExtensionSchemaId && !isEmpty(currentExtensionSchema)
  // Deliberate fallback to activity's latest schema, just in case we are
  // viewing a Job record created before 2024 which lack an extensionSchemaId.
  const activitySchema = hasCustomFields
    ? {
        id: currentExtensionSchemaId,
        ...currentExtensionSchema,
      }
    : {
        id: task?.version,
        ...task?.jobCustomFieldsSchema,
      }
  const divisionSchema = division.jobCustomFieldsSchema ?? {}

  return mergeJsonSchemas(activitySchema, divisionSchema, customFieldsSchema)
}

/**
 * Composable for managing extension schema ID versioning for jobs.

 *
 * This composable handles the logic for:
 * 1. Tracking the original extension schema ID (from the job record)
 * 2. Detecting when a newer schema version is available for the activity
 * 3. Allowing users to opt-in to use the latest schema version
 * 4. Fetching the actual schema data for the current schema ID
 *
 * Use case: When viewing/editing a job, if the activity's schema has been updated
 * since the job was created, users can choose to update to the latest schema version
 * or continue using the original version.
 *
 * @param params.originalExtensionSchemaId - The schema ID stored on the job record
 * @param params.resourceTypeActivityId - The activity ID to check for latest schema version
 * @returns An object with the current schema, schema ID, and methods to update/reset it
 */
export function useExtensionSchemaId(params: {
  originalExtensionSchemaId: MaybeRef<number | null | undefined>
  resourceTypeActivityId: MaybeRef<number | null | undefined>
}) {
  const store = useStore()

  // The schema ID originally stored on the job record
  const originalExtensionSchemaId = computed(() => unref(params.originalExtensionSchemaId))

  // Look up the latest schema version available for this activity
  const customFieldsLatestIdForActivity = computed(() => {
    const resourceTypeActivityId = unref(params.resourceTypeActivityId)
    if (resourceTypeActivityId) {
      const resourceTypeActivity = store.getters['activities/getById'](resourceTypeActivityId)
      return store.getters['tasks/getById'](resourceTypeActivity?.taskId)?.version
    }
    return null
  })

  // Local override - when user clicks "use latest schema", this gets set
  const overrideExtensionSchemaId = ref<number | null | undefined>(null)

  // The schema ID to actually use: override if set, otherwise original
  const currentExtensionSchemaId = computed(() => {
    if (overrideExtensionSchemaId.value) return overrideExtensionSchemaId.value
    return originalExtensionSchemaId.value
  })

  // Update to use the latest available schema version
  const useLatestCustomFieldsSchema = () => {
    overrideExtensionSchemaId.value = customFieldsLatestIdForActivity.value
  }

  // Reset back to the original schema version from the job record
  const resetCustomFieldsSchemaId = () => {
    overrideExtensionSchemaId.value = originalExtensionSchemaId.value
  }

  // Check if a newer schema version is available
  const hasNewerCustomFieldsSchema = computed(() => {
    return currentExtensionSchemaId.value !== customFieldsLatestIdForActivity.value
  })

  // Fetch the actual schema data for the current schema ID
  const { data: currentExtensionSchema } = useGetExtensionSchema(currentExtensionSchemaId)

  return {
    currentExtensionSchema,
    currentExtensionSchemaId,
    useLatestCustomFieldsSchema,
    resetCustomFieldsSchemaId,
    hasNewerCustomFieldsSchema,
  }
}
